/**
 * Reset step 1 — the address the code goes to. Focuses its field on arrival (including when the
 * user comes BACK here from the code step), because the dialog's content was replaced under them.
 *
 * @module @puffin-core/login-shell/components/passwordReset/ResetEmailStep
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ResetErrorAlert } from './ResetErrorAlert.jsx';
import { ResetStepIndicator } from './ResetStepIndicator.jsx';

/**
 * @param {Object} props
 * @param {Object} props.flow - `usePasswordResetFlow`'s return
 * @param {Function} props.translate
 * @param {{token: (string|null), blocked: boolean, reset: Function, field: React.ReactNode}} props.captcha - the
 *   modal's ONE widget: rendered here, and "Send code" is held while it has no required token, exactly as sign-in's
 *   submit is (PLANNER-PALM-UAT-2026-11-03-001 finish gate 7c)
 * @returns {React.JSX.Element}
 */
export function ResetEmailStep({ flow, translate, captcha }) {
  const { state } = flow;
  const emailRef = useRef(null);
  useEffect(() => {
    emailRef.current.focus();
  }, []);

  return (
    <form
      onSubmit={(event) => flow.handleInitiate(event, captcha)}
      aria-label={translate('modal.passwordReset.form.email')}
      noValidate
    >
      <ResetStepIndicator current={1} total={3} translate={translate} />
      <p className="text text-sm text-muted mb-4">
        {translate('modal.passwordReset.multiStep.emailBody')}
      </p>
      <ResetErrorAlert error={state.error} translate={translate} />
      <div className="form-group">
        <label className="form-label" htmlFor="reset-email">
          {translate('login.email.label')}
        </label>
        <input
          ref={emailRef}
          className="form-input"
          id="reset-email"
          type="email"
          value={state.email}
          onChange={(e) => flow.setField('email', e.target.value)}
          aria-required="true"
          autoComplete="email"
          disabled={state.isLoading}
          data-testid="reset-email-input"
        />
      </div>
      <div className="form-group" data-testid="reset-captcha-slot">
        {captcha.field}
      </div>
      <button
        type="submit"
        className="btn btn-primary btn-lg w-full"
        disabled={!state.email.trim() || state.isLoading || captcha.blocked}
        aria-busy={state.isLoading ? 'true' : 'false'}
        data-testid="reset-email-submit"
      >
        {state.isLoading
          ? translate('modal.passwordReset.submit.loading')
          : translate('modal.passwordReset.multiStep.emailSubmit')}
      </button>
    </form>
  );
}

ResetEmailStep.propTypes = {
  flow: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  captcha: PropTypes.shape({
    token: PropTypes.string,
    blocked: PropTypes.bool.isRequired,
    reset: PropTypes.func.isRequired,
    field: PropTypes.node.isRequired,
  }).isRequired,
};

export default ResetEmailStep;
